/**
 * Where a premise's announcement stands for a visitor at `now`. Only `live` is shown.
 *
 * - `empty` - there is no post, or it has no title. The title is what every look shows, so a post
 *   without one has nothing to show.
 * - `off` - the owner's switch (`showPublicBanner`) is off.
 * - `locked` - the switch is on, but the plan does not include announcements. Gated on read, so a
 *   downgrade hides it without touching what is stored.
 * - `scheduled` - before `showFrom` (inclusive: live from that instant).
 * - `expired` - at or after `hideAfter` (exclusive: hidden from that instant).
 *
 * docs/announcements.md has the rules.
 */
export type AnnouncementStatus = 'live' | 'scheduled' | 'expired' | 'off' | 'locked' | 'empty'

export interface AnnouncementState {
  /** `EntityEntity.showPublicBanner`. */
  published: boolean
  /** Whether the premise's plan includes `FeatureKey.Announcements`. */
  entitled: boolean
  /** The featured post, or `null` when the premise has none. */
  post: {
    title: string
    showFrom: Date | string | null
    hideAfter: Date | string | null
  } | null
}

export function announcementStatus(state: AnnouncementState, now: Date = new Date()): AnnouncementStatus {
  const { post } = state
  if (!post || post.title.trim() === '') return 'empty'
  if (!state.published) return 'off'
  if (!state.entitled) return 'locked'
  const time = now.getTime()
  if (post.showFrom != null && time < new Date(post.showFrom).getTime()) return 'scheduled'
  if (post.hideAfter != null && time >= new Date(post.hideAfter).getTime()) return 'expired'
  return 'live'
}
